/**
 * Export-only markup surface for flattened PDF capture.
 * Fixed presentation canvas + plan-only transform. HEX/RGB inline styles only.
 */
import type { CSSProperties } from "react";
import {
  BASE_MARKER_SIZE,
  markupColorHex,
  type MarkupCanvasSize,
  type MarkupPlanInformation,
  type MarkupPlanTransform,
  type PlacedMarker,
  type ProjectMarkupComponent,
  type MarkupShape,
} from "@/lib/projectMarkupTypes";
import {
  canvasPctToPx,
  computePlanRectOnCanvas,
  DEFAULT_PROJECT_MARKUP_CANVAS,
  DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM,
  drawingPctToCanvasPx,
} from "@shared/projectMarkupLayoutDocument";

export const PROJECT_MARKUP_EXPORT_ROOT_ATTR = "data-project-markup-export-canvas";

const C = {
  bg: "#ffffff",
  text: "#111111",
  muted: "#525252",
  border: "#d1d5db",
  placeholderBg: "#f4f4f5",
} as const;

function ExportMarkerShape({
  shape,
  color,
  size,
}: {
  shape: MarkupShape;
  color: string;
  size: number;
}) {
  if (shape === "triangle") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden>
        <polygon points="8,1 15,15 1,15" fill={color} stroke="#ffffff" strokeWidth="1" />
      </svg>
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        border: "2px solid #ffffff",
        borderRadius: shape === "circle" ? "9999px" : "2px",
        boxSizing: "border-box",
      }}
    />
  );
}

function ExportProductThumb({ src, alt }: { src: string | null; alt: string }) {
  const box: CSSProperties = {
    width: 28,
    height: 28,
    flexShrink: 0,
    boxSizing: "border-box",
    borderRadius: 3,
    border: `1px solid ${C.border}`,
  };

  if (!src) {
    return <div style={{ ...box, backgroundColor: C.placeholderBg }} aria-hidden />;
  }

  return (
    <div style={{ position: "relative", width: 28, height: 28, flexShrink: 0 }}>
      <div
        style={{
          ...box,
          position: "absolute",
          inset: 0,
          backgroundColor: C.placeholderBg,
        }}
        aria-hidden
      />
      <img
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        style={{
          ...box,
          position: "relative",
          objectFit: "contain",
          backgroundColor: "#ffffff",
        }}
        onError={(e) => {
          e.currentTarget.style.visibility = "hidden";
        }}
      />
    </div>
  );
}

export type ProjectMarkupExportCanvasProps = {
  planImageDataUrl: string;
  /** Natural plan bitmap size (for aspect / fit). */
  planNaturalWidth: number;
  planNaturalHeight: number;
  markers: PlacedMarker[];
  componentsById: Map<string, ProjectMarkupComponent>;
  canvas?: MarkupCanvasSize;
  planTransform?: MarkupPlanTransform;
  planInformation?: MarkupPlanInformation | null;
};

export default function ProjectMarkupExportCanvas({
  planImageDataUrl,
  planNaturalWidth,
  planNaturalHeight,
  markers,
  componentsById,
  canvas = DEFAULT_PROJECT_MARKUP_CANVAS,
  planTransform = DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM,
  planInformation,
}: ProjectMarkupExportCanvasProps) {
  const width = Math.max(1, Math.round(canvas.width));
  const height = Math.max(1, Math.round(canvas.height));
  const planRect = computePlanRectOnCanvas({
    canvas: { width, height },
    planNaturalWidth: Math.max(1, planNaturalWidth),
    planNaturalHeight: Math.max(1, planNaturalHeight),
    planTransform,
  });

  return (
    <div
      {...{ [PROJECT_MARKUP_EXPORT_ROOT_ATTR]: "true" }}
      style={{
        position: "relative",
        width,
        height,
        backgroundColor: C.bg,
        color: C.text,
        fontFamily: "Arial, Helvetica, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* LAYER 2 — floor plan only */}
      <div
        style={{
          position: "absolute",
          left: planRect.left,
          top: planRect.top,
          width: planRect.width,
          height: planRect.height,
        }}
      >
        <img
          src={planImageDataUrl}
          alt="Floor plan"
          crossOrigin="anonymous"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "fill",
            backgroundColor: C.bg,
          }}
        />
      </div>

      {/* Connectors: drawing marker → canvas callout */}
      <svg
        width={width}
        height={height}
        style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
        aria-hidden
      >
        {markers.map((marker) => {
          const color = markupColorHex(marker.color);
          const from = drawingPctToCanvasPx(marker.x, marker.y, planRect);
          const to = canvasPctToPx(marker.calloutX, marker.calloutY, { width, height });
          return (
            <line
              key={`line-${marker.id}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={color}
              strokeWidth={2}
              strokeDasharray="4 3"
              fill="none"
            />
          );
        })}
      </svg>

      {/* Markers in drawing space */}
      {markers.map((marker) => {
        const component = componentsById.get(marker.componentId);
        if (!component) return null;
        const color = markupColorHex(marker.color);
        const markerSize = BASE_MARKER_SIZE * (marker.markerScale ?? 1);
        const at = drawingPctToCanvasPx(marker.x, marker.y, planRect);
        return (
          <div
            key={`mk-${marker.id}`}
            style={{
              position: "absolute",
              left: at.x - markerSize / 2,
              top: at.y - markerSize / 2,
              width: markerSize,
              height: markerSize,
            }}
          >
            <ExportMarkerShape shape={marker.shape} color={color} size={markerSize} />
          </div>
        );
      })}

      {/* Callouts in canvas space — auto-grow, no text clipping */}
      {markers.map((marker) => {
        const component = componentsById.get(marker.componentId);
        if (!component) return null;
        const color = markupColorHex(marker.color);
        const calloutW = marker.calloutWidth;
        const calloutMinH = marker.calloutHeight;
        const center = canvasPctToPx(marker.calloutX, marker.calloutY, { width, height });
        const calloutLeft = center.x - calloutW / 2;
        const calloutTop = center.y - calloutMinH / 2;

        return (
          <div
            key={`co-${marker.id}`}
            style={{
              position: "absolute",
              left: calloutLeft,
              top: calloutTop,
              width: calloutW,
              minHeight: calloutMinH,
              boxSizing: "border-box",
              backgroundColor: "#ffffff",
              border: `2px solid ${color}`,
              borderRadius: 5,
              padding: 5,
              overflow: "visible",
            }}
          >
            <div style={{ display: "flex", gap: 5, alignItems: "flex-start" }}>
              <ExportProductThumb src={component.imageUrl} alt={component.name} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: "13px",
                    color: C.text,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {component.name}
                </div>
                {component.subtitle ? (
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 9,
                      lineHeight: "11px",
                      color: C.muted,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {component.subtitle}
                  </div>
                ) : null}
                <div style={{ marginTop: 2, fontSize: 9, lineHeight: "11px", color: C.muted }}>
                  {component.dimensionsLabel ?? "Dimensions not set"}
                </div>
                {component.weightLabel ? (
                  <div style={{ marginTop: 1, fontSize: 9, lineHeight: "11px", color: C.muted }}>
                    {component.weightLabel}
                  </div>
                ) : null}
                <div style={{ marginTop: 2, fontSize: 9, lineHeight: "11px", color: C.text, fontWeight: 600 }}>
                  Qty {component.qty}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {planInformation ? (
        <div
          style={{
            position: "absolute",
            left: canvasPctToPx(planInformation.x, planInformation.y, { width, height }).x - planInformation.width / 2,
            top: canvasPctToPx(planInformation.x, planInformation.y, { width, height }).y - planInformation.height / 2,
            width: planInformation.width,
            height: planInformation.height,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            backgroundColor: "#ffffff",
            padding: 8,
            boxSizing: "border-box",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <img
            src="/assets/clarity-logo.svg"
            alt="Clarity"
            style={{ width: 52, objectFit: "contain", flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, lineHeight: "13px" }}>
              {planInformation.title || "Plan Title"}
            </div>
            <div style={{ marginTop: 3, fontSize: 10, color: C.muted, lineHeight: "12px" }}>
              Prepared By: {planInformation.preparedBy || "Staff"}
            </div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: "12px" }}>
              Date: {planInformation.date || "—"}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
